import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { Award, Star, X, Check, Lock } from 'lucide-react';

const FeaturedBadgeSelector = ({ onClose, onSave }) => {
  const [myBadges, setMyBadges] = useState([]);
  const [featuredBadges, setFeaturedBadges] = useState([]);
  const [selectedBadges, setSelectedBadges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [lockedBadge, setLockedBadge] = useState(null);

  useEffect(() => {
    fetchBadges();
  }, []);

  const fetchBadges = async () => {
    try {
      const [myBadgesRes, featuredRes] = await Promise.all([
        api.get('/badges/my-badges'),
        api.get('/users/my-featured-badges')
      ]);

      const earned = myBadgesRes.data.badges || [];
      const featured = featuredRes.data.featured_badges || [];
      
      setMyBadges(earned);
      setFeaturedBadges(featured);
      
      // Check if user has Admin or Community Leader badge (these are LOCKED)
      const adminBadge = earned.find(b => b.badge_code === 'site-admin' || b.code === 'site-admin');
      const leaderBadge = earned.find(b => b.badge_code === 'community-leader' || b.code === 'community-leader');
      const locked = adminBadge || leaderBadge;
      
      if (locked) {
        setLockedBadge(locked);
        // User has a locked badge - pre-select it and only allow 2 more
        const lockedUrl = locked.badge_icon_url || locked.icon_url;
        const otherSelected = featured
          .map(f => f.badge_url)
          .filter(url => url !== lockedUrl)
          .slice(0, 2);
        
        setSelectedBadges([lockedUrl, ...otherSelected]);
      } else {
        setLockedBadge(null);
        setSelectedBadges(featured.map(f => f.badge_url));
      }
    } catch (error) {
      console.error('Error fetching badges:', error);
      toast.error('Failed to load badges');
    } finally {
      setLoading(false);
    }
  };

  const toggleBadge = (badgeUrl, badge) => {
    // Check if this is the locked badge
    if (lockedBadge) {
      const lockedUrl = lockedBadge.badge_icon_url || lockedBadge.icon_url;
      if (badgeUrl === lockedUrl) {
        toast.info('This badge is always displayed and cannot be removed');
        return;
      }
    }

    if (selectedBadges.includes(badgeUrl)) {
      // Remove badge (but not if it's the first locked one)
      setSelectedBadges(selectedBadges.filter(b => b !== badgeUrl));
    } else {
      // Add badge (max 3, or max 2 if has locked badge)
      const maxBadges = lockedBadge ? 3 : 3; // Always 3 total, but 1 is locked if admin/leader
      if (selectedBadges.length >= maxBadges) {
        const message = lockedBadge 
          ? 'You can only select 2 additional badges (1 slot is reserved for your admin/leader badge)'
          : 'You can only select up to 3 featured badges';
        toast.warning(message);
        return;
      }
      setSelectedBadges([...selectedBadges, badgeUrl]);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/users/my-featured-badges', {
        badge_urls: selectedBadges
      });
      
      toast.success('Featured badges updated!');
      if (onSave) onSave();
      if (onClose) onClose();
    } catch (error) {
      console.error('Error saving featured badges:', error);
      toast.error('Failed to save featured badges');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-8 max-w-2xl w-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#C5A52A] mx-auto mb-4"></div>
            <p className="font-mono text-sm text-gray-500">Loading your badges...</p>
          </div>
        </div>
      </div>
    );
  }

  const lockedUrl = lockedBadge ? (lockedBadge.badge_icon_url || lockedBadge.icon_url) : null;
  const maxSelectable = lockedBadge ? 2 : 3;
  const availableSlots = lockedBadge ? selectedBadges.length - 1 : selectedBadges.length;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4 overflow-y-auto">
      <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 max-w-3xl w-full my-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Star className="w-6 h-6 text-[#C5A52A]" />
            <h2 className="font-headline text-2xl text-white">SELECT FEATURED BADGES</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-white transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Instructions */}
        <div className="bg-blue-900/20 border border-blue-500/30 rounded-lg p-4 mb-6">
          {lockedBadge ? (
            <>
              <div className="flex items-center gap-2 mb-2">
                <Lock className="w-4 h-4 text-yellow-400" />
                <p className="font-mono text-sm text-yellow-400 font-bold">
                  Special Badge Status
                </p>
              </div>
              <p className="font-mono text-xs text-blue-300">
                Your {lockedBadge.badge_name || lockedBadge.name} badge is always displayed. 
                Select {maxSelectable} additional badges to showcase.
              </p>
              <p className="font-mono text-xs text-blue-400 mt-2">
                Selected: {availableSlots}/{maxSelectable} additional badges
              </p>
            </>
          ) : (
            <>
              <p className="font-mono text-sm text-blue-400">
                ⭐ Choose up to 3 badges to display next to your name throughout the site.
              </p>
              <p className="font-mono text-xs text-blue-300 mt-2">
                Selected: {selectedBadges.length}/3
              </p>
            </>
          )}
        </div>

        {/* Locked Badge Display */}
        {lockedBadge && (
          <div className="mb-6 bg-yellow-900/20 border border-yellow-500/30 rounded-lg p-4">
            <div className="flex items-center gap-3 mb-2">
              <Lock className="w-5 h-5 text-yellow-400" />
              <h3 className="font-mono text-sm text-yellow-400 font-bold">
                Always Displayed
              </h3>
            </div>
            <div className="flex items-center gap-3 bg-[#111] border border-yellow-500/50 rounded-lg p-3">
              <div>
                {lockedUrl && lockedUrl.startsWith('http') ? (
                  <img
                    src={lockedUrl}
                    alt={lockedBadge.badge_name || lockedBadge.name}
                    className="w-12 h-12 object-contain"
                  />
                ) : (
                  <div className="text-3xl">{lockedUrl || '🏅'}</div>
                )}
              </div>
              <div>
                <p className="font-mono text-sm text-white font-bold">
                  {lockedBadge.badge_name || lockedBadge.name}
                </p>
                <p className="font-mono text-xs text-gray-400">This badge cannot be removed</p>
              </div>
            </div>
          </div>
        )}

        {/* Badge Grid */}
        {myBadges.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 mb-6 max-h-96 overflow-y-auto pr-2">
            {myBadges.map((badge) => {
              const badgeUrl = badge.badge_icon_url || badge.icon_url;
              const isSelected = selectedBadges.includes(badgeUrl);
              const isLocked = badgeUrl === lockedUrl;

              return (
                <button
                  key={badge.id}
                  onClick={() => toggleBadge(badgeUrl, badge)}
                  disabled={isLocked}
                  className={`relative bg-[#111] border-2 rounded-lg p-4 transition-all ${
                    isLocked
                      ? 'border-yellow-500 bg-yellow-500/10 cursor-not-allowed opacity-75'
                      : isSelected
                      ? 'border-[#C5A52A] bg-[#C5A52A]/10 hover:scale-105'
                      : 'border-gray-800 hover:border-gray-700 hover:scale-105'
                  }`}
                >
                  {/* Selection Indicator */}
                  {isSelected && !isLocked && (
                    <div className="absolute top-2 right-2 bg-[#C5A52A] rounded-full p-1">
                      <Check className="w-3 h-3 text-black" />
                    </div>
                  )}

                  {/* Locked Indicator */}
                  {isLocked && (
                    <div className="absolute top-2 right-2 bg-yellow-500 rounded-full p-1">
                      <Lock className="w-3 h-3 text-black" />
                    </div>
                  )}

                  {/* Badge Icon */}
                  <div className="text-center mb-2">
                    {badgeUrl && badgeUrl.startsWith('http') ? (
                      <img
                        src={badgeUrl}
                        alt={badge.badge_name}
                        className="w-12 h-12 mx-auto object-contain"
                      />
                    ) : (
                      <div className="text-4xl">{badgeUrl || '🏅'}</div>
                    )}
                  </div>

                  {/* Badge Name */}
                  <p className="font-mono text-xs text-gray-400 line-clamp-2">
                    {badge.badge_name}
                  </p>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-12">
            <Award className="w-12 h-12 text-gray-700 mx-auto mb-4" />
            <p className="font-mono text-sm text-gray-500">
              You haven't earned any badges yet.
            </p>
            <p className="font-mono text-xs text-gray-600 mt-2">
              Start engaging to earn badges!
            </p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <button
            onClick={onClose}
            className="px-6 py-3 rounded border border-gray-700 text-gray-400 hover:text-white hover:border-gray-600 transition-colors font-mono text-sm"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`px-6 py-3 rounded font-mono text-sm transition-colors ${
              saving
                ? 'bg-gray-800 text-gray-500 cursor-not-allowed'
                : 'bg-[#C5A52A] hover:bg-[#D4B93A] text-black'
            }`}
          >
            {saving ? 'Saving...' : 'Save Featured Badges'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeaturedBadgeSelector;
